import { useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import {
  BedDouble,
  BookUser,
  CalendarCheck,
  CalendarPlus,
  CheckCircle2,
  ClipboardList,
  Loader2,
  PhoneCall,
  Receipt,
  RefreshCw,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { parseDateOnly } from "@/lib/datetime"
import { cn } from "@/lib/utils"
import { tcmErrorMessage } from "../api"
import { useTcmBoard, useTcmBoards } from "../hooks"
import {
  DONE_GROUPS,
  STAGE_GROUPS,
  TILES,
  groupByStage,
  groupByVisitDay,
  matchesTile,
  tileCounts,
  type TileId,
} from "../lib/board"
import type { TcmEpisode } from "../types"
import { TcmCaseDrawer } from "./TcmCaseDrawer"
import { TcmCaseTable } from "./TcmCaseTable"
import { ClockHint } from "./TcmClock"
import { TcmClinicCards } from "./TcmClinicCards"
import { useCopyTcmNote } from "./useCopyTcmNote"
import { useTcmView } from "./useTcmView"

const TILE_ICONS: Record<TileId, React.ElementType> = {
  contact: PhoneCall,
  book: CalendarPlus,
  week: CalendarCheck,
  bill: Receipt,
  hospital: BedDouble,
}

type TabId = "active" | "visits" | "closed"

function forClinic(episodes: TcmEpisode[], organizationId: string | null): TcmEpisode[] {
  return organizationId ? episodes.filter((e) => e.organization_id === organizationId) : episodes
}

export function TcmPage() {
  const { orgId, summary, clinics, pearView, ehrFor } = useTcmView()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<TabId>("active")
  const [tile, setTile] = useState<TileId | null>(null)
  const [clinicFilter, setClinicFilter] = useState<string | null>(null)

  useEffect(() => setClinicFilter(null), [orgId])

  const clinicIds = useMemo(() => (pearView ? clinics.map((c) => c.organization_id) : []), [pearView, clinics])
  const own = useTcmBoard(pearView || summary.isLoading ? null : orgId, "active")
  const ownClosed = useTcmBoard(!pearView && tab === "closed" ? orgId : null, "closed")
  const all = useTcmBoards(clinicIds, "active", pearView)
  const allClosed = useTcmBoards(clinicIds, "closed", pearView && tab === "closed")

  const clinicNames = useMemo(
    () => Object.fromEntries(clinics.map((c) => [c.organization_id, c.organization_name ?? "Clinic"])),
    [clinics],
  )
  const allEpisodes = useMemo(() => all.flatMap((q) => q.data?.episodes ?? []), [all])
  const allClosedEpisodes = useMemo(() => allClosed.flatMap((q) => q.data?.episodes ?? []), [allClosed])
  const episodes = useMemo(
    () => (pearView ? forClinic(allEpisodes, clinicFilter) : own.data?.episodes ?? []),
    [pearView, allEpisodes, own.data, clinicFilter],
  )
  const closedEpisodes = useMemo(
    () => (pearView ? forClinic(allClosedEpisodes, clinicFilter) : ownClosed.data?.episodes ?? []),
    [pearView, allClosedEpisodes, ownClosed.data, clinicFilter],
  )

  const todayIso = pearView ? summary.data?.today : own.data?.today
  const today = useMemo(() => parseDateOnly(todayIso) ?? new Date(), [todayIso])
  const counts = useMemo(() => tileCounts(episodes, today), [episodes, today])
  const { copy, copyingId } = useCopyTcmNote()

  const openCase = searchParams.get("case")
  const setOpenCase = (id: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (id) next.set("case", id)
    else next.delete("case")
    setSearchParams(next, { replace: true })
  }

  const activeGroups = useMemo(() => {
    const rows = tile ? episodes.filter((ep) => matchesTile(ep, tile, today)) : episodes
    return groupByStage(rows, STAGE_GROUPS)
  }, [episodes, tile, today])
  const visitGroups = useMemo(() => groupByVisitDay(episodes, today), [episodes, today])
  const closedGroups = useMemo(() => groupByStage(closedEpisodes, DONE_GROUPS), [closedEpisodes])

  if (!orgId) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select an organization to view transitional care
      </div>
    )
  }

  const activeLoading = summary.isLoading || (pearView ? all.some((q) => q.isLoading) : own.isLoading)
  const closedLoading = pearView ? allClosed.some((q) => q.isLoading) : ownClosed.isLoading
  const fetching = pearView ? all.some((q) => q.isFetching) || allClosed.some((q) => q.isFetching) : own.isFetching || ownClosed.isFetching
  const error = pearView ? all.find((q) => q.error)?.error : own.error
  const refresh = () => {
    summary.refetch()
    if (pearView) {
      all.forEach((q) => q.refetch())
      if (tab === "closed") allClosed.forEach((q) => q.refetch())
    } else {
      own.refetch()
      if (tab === "closed") ownClosed.refetch()
    }
  }
  const ehrLabel = pearView ? "each clinic's EHR" : ehrFor(orgId)
  const selectedTile = TILES.find((t) => t.id === tile)
  const rowClinic = pearView && !clinicFilter ? (id: string) => clinicNames[id] : undefined
  const table = { today: todayIso ?? "", ehrFor, clinicName: rowClinic, onOpen: setOpenCase, onCopy: copy, copyingId }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Transitional Care</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {pearView
              ? `Every clinic's discharges, where each is in its 30 days, and the note ready to paste into ${ehrLabel}.`
              : `Every discharge, where it is in its 30 days, and the note ready to paste into ${ehrLabel}.`}
          </p>
        </div>
        <div className="flex gap-2 self-start md:self-auto">
          {!pearView && (
            <Button asChild variant="ghost" size="sm" className="gap-2">
              <Link to="/tcm/resources">
                <BookUser className="h-3.5 w-3.5" />
                Resource directory
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={fetching} className="gap-2">
            {fetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>
      </div>

      {pearView && <TcmClinicCards clinics={clinics} selectedId={clinicFilter} onSelect={setClinicFilter} />}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {TILES.map((t) => {
          const Icon = TILE_ICONS[t.id]
          const c = counts[t.id]
          const on = tile === t.id
          const sub = t.id === "week" && c.count ? `${c.notesReady} of ${c.count} notes ready` : t.sub
          return (
            <button
              key={t.id}
              type="button"
              aria-pressed={on}
              onClick={() => {
                setTile(on ? null : t.id)
                setTab("active")
              }}
              className={cn(
                "rounded-xl border bg-card p-4 md:p-5 text-left transition-colors hover:bg-muted/40",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                on && "ring-2 ring-primary border-primary",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-muted-foreground">{t.label}</p>
                  <p
                    className={cn(
                      "mt-1 text-2xl font-semibold tabular-nums",
                      c.count === 0 ? "text-muted-foreground" : c.urgent ? "text-destructive" : "text-foreground",
                    )}
                  >
                    {activeLoading ? "-" : c.count}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>
                </div>
                <div className="rounded-lg bg-accent p-2 text-accent-foreground">
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Could not load TCM cases: {tcmErrorMessage(error)}
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList className="bg-muted/50 p-0.5">
            <TabsTrigger value="active" className="text-xs gap-1.5">
              <ClipboardList className="h-3 w-3" />
              Active
              {episodes.length > 0 && (
                <span className="rounded-full bg-muted px-1.5 py-px text-[10px] font-semibold leading-tight tabular-nums">
                  {episodes.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="visits" className="text-xs gap-1.5">
              <CalendarCheck className="h-3 w-3" />
              Upcoming visits
            </TabsTrigger>
            <TabsTrigger value="closed" className="text-xs gap-1.5">
              <CheckCircle2 className="h-3 w-3" />
              Billed &amp; closed
            </TabsTrigger>
          </TabsList>
          {tab === "active" && selectedTile && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setTile(null)}>
              Showing {selectedTile.label.toLowerCase()} · Show all
            </Button>
          )}
        </div>

        <TabsContent value="active" className="mt-5 space-y-3">
          <ClockHint />
          {activeLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <TcmCaseTable
              {...table}
              groups={activeGroups}
              emptyText={tile ? "Nothing here right now." : pearView ? "No open TCM cases." : "No open TCM cases for this clinic."}
            />
          )}
        </TabsContent>

        <TabsContent value="visits" className="mt-5">
          <TcmCaseTable {...table} groups={visitGroups} emptyText="No follow-up visits booked in the next 14 days." />
        </TabsContent>

        <TabsContent value="closed" className="mt-5">
          {closedLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <TcmCaseTable {...table} groups={closedGroups} emptyText="No billed or closed cases in the last 90 days." />
          )}
        </TabsContent>
      </Tabs>

      <TcmCaseDrawer
        episodeId={openCase}
        today={todayIso ?? ""}
        ehrFor={ehrFor}
        onClose={() => setOpenCase(null)}
        onCopy={copy}
        copying={!!openCase && copyingId === openCase}
      />
    </div>
  )
}
